import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { ErrorBoundary } from "@/components/organisms/ErrorBoundary";
import { ChatGatewaySyncProgress } from "@/components/organisms/ChatGatewaySyncProgress";
import { PageLoader } from "@/components/atoms/PageLoader";
import { useUiStore } from "@/store/uiStore";
import { selectEffectiveTheme, useThemeStore } from "@/store/themeStore";
import { Bounce, ToastContainer } from "react-toastify";
import { ConversationPanel } from "@/modules/conversation/ConversationPanel";
import { TenantSelectorModal } from "@/components/organisms/TenantSelectorModal";
import { useAppKeyboardShortcuts } from "@/hooks/useAppKeyboardShortcuts";

export function AppLayout() {
  useAppKeyboardShortcuts();
  const { sidebarCollapsed } = useUiStore();
  const theme = useThemeStore(selectEffectiveTheme);
  const setLightLocked = useThemeStore((s) => s.setLightLocked);
  const location = useLocation();
  const isConversationsRoute = location.pathname.replace(/\/$/, "") === "/conversations";
  const [hasOpenedConversations, setHasOpenedConversations] = useState(false);

  useEffect(() => {
    if (isConversationsRoute) {
      setHasOpenedConversations(true);
    }
  }, [isConversationsRoute]);

  // The Conversations iframe (Chatwoot) has no dark mode, so a dark shell around
  // it would clash — hold the app in light mode while it's on screen. The saved
  // preference is untouched and comes back on leaving the tab.
  useEffect(() => {
    setLightLocked(isConversationsRoute);
    return () => setLightLocked(false);
  }, [isConversationsRoute, setLightLocked]);

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-bridge-bg">
      <PageLoader />
      {/* Sidebar overlays content */}
      <div className="fixed top-0 left-0 h-screen z-30">
        <Sidebar />
      </div>
      {/* Topbar stays at the top, full width */}
      <div
        className="fixed top-0 left-0 right-0 h-[56px] border-b border-bridge-border bg-white z-[60] transition-[margin] duration-300 ease-in-out"
        style={{ marginLeft: sidebarCollapsed ? "72px" : "240px" }}
      >
        <Topbar />
      </div>
      {/* Main content fills the screen, with left padding for sidebar when open */}
      <main
        className="absolute top-[56px] left-0 right-0 bottom-0 min-w-0 h-[calc(100vh-56px)] overflow-hidden z-0 transition-[margin] duration-300 ease-in-out"
        style={{ marginLeft: sidebarCollapsed ? "72px" : "240px" }}
      >
        <ErrorBoundary>
          <div
            className="h-full w-full overflow-y-auto"
            style={{ display: isConversationsRoute ? "none" : "block" }}
          >
            <Outlet />
          </div>
          {hasOpenedConversations && (
            <div 
              className="h-full w-full overflow-hidden"
              style={{ display: isConversationsRoute ? "block" : "none" }}
            >
              <ConversationPanel />
            </div>
          )}
        </ErrorBoundary>
      </main>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme}
        transition={Bounce}
        style={{ zIndex: 10000 }}
      />
      <ChatGatewaySyncProgress />
      <TenantSelectorModal />
    </div>
  );
}